import {Injectable} from '@angular/core';
import FroalaEditor from 'froala-editor';

/**
 * Provides the toolbar html of a real froala editor for given editor options.
 * Used by the lazy froala component to render a pixel perfect static toolbar
 * preview without initializing an editor per field (MYM-1029).
 *
 * The toolbar html is obtained by initializing a single hidden froala editor
 * per toolbar configuration, snapshotting its toolbar dom and destroying it
 * again. Results are cached, so each configuration is only rendered once.
 */
@Injectable()
export class FroalaToolbarPreviewService {
  /**
   * Cached toolbar html promises by toolbar configuration signature
   */
  private cache: {[signature: string]: Promise<string>} = {};

  /**
   * Returns the toolbar html for given froala editor options
   *
   * @param {any} options Froala editor options
   * @returns {Promise<string>}
   */
  public getToolbarHtml(options: any): Promise<string> {
    const signature = JSON.stringify((options && options.toolbarButtons) || 'default');

    if (!this.cache[signature]) {
      this.cache[signature] = this.snapshotToolbar(options).catch(() => '');
    }

    return this.cache[signature];
  }

  /**
   * Initializes a hidden froala editor with given options, extracts the
   * toolbar html and destroys the editor again
   *
   * @param {any} options Froala editor options
   * @returns {Promise<string>}
   */
  private snapshotToolbar(options: any): Promise<string> {
    return new Promise<string>((resolve) => {
      const host = document.createElement('div');
      host.style.cssText = 'position:absolute;left:-99999px;top:0;width:800px;';

      const el = document.createElement('div');
      host.appendChild(el);
      document.body.appendChild(host);

      // silent copy of the options: no custom event handlers, no char counter, no placeholder
      const opts = Object.assign({}, options, {
        events: {},
        charCounterCount: false,
        placeholderText: '',
        initOnClick: false
      });

      const editor: any = new (FroalaEditor as any)(el, opts, function () {
        const toolbar = host.querySelector('.fr-toolbar');

        // strip ids and aria references, as this html gets injected into multiple previews
        const html = toolbar ? toolbar.outerHTML.replace(/\s(?:id|aria-controls)="[^"]*"/g, '') : '';

        try {
          editor.destroy();
        } catch (e) {
          // ignore, the host element gets removed anyway
        }

        host.parentNode && host.parentNode.removeChild(host);
        resolve(html);
      });
    });
  }
}
